import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { catalogs } from 'store';
import { isConfigDiskFulfilled, orderId, sspSubmitOrder, toggleSspModels } from 'shared/store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import getProvisioningTitle from 'utils/getProvisioningTitle';
import { formOrderObjFromCart, formOrderObjFromOrderDetails, resetOrder, resetToggleSspModal } from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/azure/linux/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/azure/linux/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/azure/linux/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/azure/linux/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/azure/linux/ShowCostDetails'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/azure/linux/ConfirmDD'));
// const ManageDataDisk = React.lazy(() => import('pages/provisioning/azure/linux/components/Form/VmDetailsSection/DataDiskModal'));
const PgtDataDiskModal = React.lazy(() => import('pages/provisioning/azure/linux/components/Form/VmDetailsSection/PgtDataDiskModal'));
const Tags = React.lazy(() => import('pages/provisioning/azure/linux/Tags'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/azure/linux/ApplicationDetails'));

const LinuxProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const { osType, serverType, catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axiosInstance = useAxios();

    const resourceGroupApi = useMutation((payload) => axiosInstance.get(endPoints.mySql.resourceGroup(payload)));

    async function getCosts() {
        const { data } = await axiosInstance.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axiosInstance.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, osType, serverType, data?.images?.sku);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
            resetIsConfigDiskFulfilled(true);
        } else if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axiosInstance.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails, osType, serverType, data?.images?.sku);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
            resetIsConfigDiskFulfilled(true);
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                sku: data?.images?.sku,
                osType,
                serverType,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({
            ...order,
            ...resetOrder,
        });
        resetToggleSspModels(resetToggleSspModal);
        resetIsConfigDiskFulfilled(false);
        resetOrderId('');
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>{getProvisioningTitle(cmpCatalogs, catalogCode)}</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox} data-testid="aws/dynamo-db-wrapper">
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                        <ConfirmDD />
                        {/* <ManageDataDisk /> */}
                        <PgtDataDiskModal />
                        <Tags />
                        <ApplicationDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default LinuxProvisioningPage;
